/**
 * Auth0 Desktop Event Handling
 *
 * Centralizes all Tauri auth event constants, types, and listener setup.
 * This provides a single source of truth for auth events and simplifies
 * the provider's event handling logic.
 */

import { logger } from '@/utils/logger/logger';
import type {
  DesktopAuthErrorEventPayload,
  DesktopAuthSession,
  DesktopDownloadAttributionPayload,
} from './index';

/**
 * Auth event names emitted by the Rust backend.
 * Using constants eliminates magic strings and enables autocomplete.
 */
export const AUTH_EVENTS = {
  /** Emitted when login completes successfully with session data */
  LOGIN_SUCCESS: 'auth:login-success',
  /** Emitted when an auth error occurs (login failure, token refresh failure, etc.) */
  ERROR: 'auth:error',
  /** Emitted when logout completes (reserved for future use) */
  LOGOUT_SUCCESS: 'auth:logout-success',
  /** Emitted after a token refresh succeeds and is persisted. Notification-only — no payload; a listener re-reads the session via the auth_session command. */
  SESSION_REFRESHED: 'auth:session-refreshed',
  /**
   * Emitted after login when the post-sign-in page forwarded download
   * attribution (SCR-5478). Carries `DownloadAttributionEvent` — deliberately
   * separate from LOGIN_SUCCESS so untrusted attribution never rides on the
   * credential response. Consumed by Layer 3 (validate before use).
   */
  DOWNLOAD_ATTRIBUTION: 'auth:download-attribution',
} as const;

export type AuthEventName = (typeof AUTH_EVENTS)[keyof typeof AUTH_EVENTS];

/**
 * Handlers for auth events
 */
export interface AuthEventHandlers {
  onLoginSuccess: (session: DesktopAuthSession) => void | Promise<void>;
  onError: (payload: DesktopAuthErrorEventPayload) => void;
  /**
   * Fires after login when the post-sign-in page forwarded download
   * attribution (SCR-5478). Optional: most logins carry no attribution
   * (organic / Apple-login / cross-browser / consent-denied users).
   */
  onDownloadAttribution?: (payload: DesktopDownloadAttributionPayload) => void;
}

/**
 * Sets up listeners for all auth-related Tauri events.
 *
 * @param handlers - Callback handlers for each event type
 * @returns A cleanup function to unsubscribe from all events
 *
 * @example
 * ```typescript
 * const cleanup = await setupAuthEventListeners({
 *   onLoginSuccess: (session) => {
 *     setAuthSession(session);
 *     router.push('/scribe');
 *   },
 *   onError: (error) => {
 *     showErrorToast(error.description);
 *   },
 * });
 *
 * // Later, to clean up:
 * cleanup();
 * ```
 */
export async function setupAuthEventListeners(
  handlers: AuthEventHandlers
): Promise<() => void> {
  try {
    const { listen } = await import('@tauri-apps/api/event');

    const unlisteners = await Promise.all([
      listen<DesktopAuthErrorEventPayload>(AUTH_EVENTS.ERROR, (event) => {
        handlers.onError(event.payload ?? {});
      }),
      listen<DesktopAuthSession>(AUTH_EVENTS.LOGIN_SUCCESS, (event) => {
        // Handler may be async, but we don't need to await it here
        Promise.resolve(handlers.onLoginSuccess(event.payload)).catch((error) => {
          logger.error('[setupAuthEventListeners] Login success handler failed', error);
        });
      }),
      listen<DesktopDownloadAttributionPayload>(
        AUTH_EVENTS.DOWNLOAD_ATTRIBUTION,
        (event) => {
          handlers.onDownloadAttribution?.(event.payload);
        }
      ),
    ]);

    return () => {
      for (const unlisten of unlisteners) {
        unlisten();
      }
    };
  } catch (error) {
    logger.warn('[setupAuthEventListeners] Failed to setup auth event listeners', error);
    // Return no-op cleanup function
    return () => {};
  }
}

/**
 * Bounded error label for a telemetry field.
 *
 * `error.name` and nothing else: per `.ai-context/telemetry-phi-pii-safety.md`
 * an auth/IdP failure routinely echoes the account email or IdP subject in
 * `message`, and the Datadog transport applies no redaction to free text.
 * `sanitizeError` is not the tool here — it projects a non-axios error to `{}`,
 * so a Tauri event-API rejection would carry no signal at all.
 */
function rejectionName(results: readonly PromiseSettledResult<unknown>[]): string {
  const rejection = results.find(
    (result): result is PromiseRejectedResult => result.status === 'rejected'
  );
  if (rejection === undefined) {
    // Defensive only, and unreachable from both call sites: the all-failed
    // branch runs when every result rejected, and the partial branch when at
    // least one did. It exists so the helper stays total for its own signature
    // rather than because a caller can land here.
    return 'None';
  }
  return rejection.reason instanceof Error ? rejection.reason.name : 'UnknownError';
}

/**
 * Subscribe to every moment the Rust plugin has just written a credential to
 * its store — a login callback commit and a token refresh commit.
 *
 * Both of those run `persist_session`, which is the only writer of the
 * keychain cached identity. Consumers that key off that identity cannot use an
 * `isAuthenticated` boolean instead: it transitions when a session is
 * *created*, not when one is *replaced*, so a Kinde→Auth0 desktop migration
 * and a silent refresh both leave it `true` throughout.
 *
 * Deliberately payload-less. `auth:session-refreshed` carries nothing by
 * design, and the login session is already owned by
 * {@link setupAuthEventListeners}; a second consumer of the credential would
 * be a second place to keep in step with it. Callers re-read whatever they
 * need through their own bridge.
 *
 * Resolves to a no-op unlisten when the Tauri event API is unavailable, so
 * cleanup is always safe.
 */
export async function listenDesktopAuthSessionCommitted(
  onCommitted: () => void
): Promise<() => void> {
  try {
    const { listen } = await import('@tauri-apps/api/event');

    // Settled individually rather than through `Promise.all`: there, one
    // rejection discards the unlisten handle of whichever subscription DID
    // attach, so that listener survives with no way to tear it down and every
    // effect re-run stacks another one on top of it.
    const results = await Promise.allSettled([
      listen(AUTH_EVENTS.LOGIN_SUCCESS, () => onCommitted()),
      listen(AUTH_EVENTS.SESSION_REFRESHED, () => onCommitted()),
    ]);

    const unlisteners = results.flatMap((result) =>
      result.status === 'fulfilled' ? [result.value] : []
    );

    if (unlisteners.length === 0) {
      logger.warn(
        '[listenDesktopAuthSessionCommitted] Failed to subscribe to auth session events',
        { errorName: rejectionName(results) }
      );
      return () => {};
    }

    if (unlisteners.length < results.length) {
      // Partial attach is kept, not discarded: re-arming on one of the two
      // commit events still beats re-arming on neither, and the caller now
      // holds a handle for exactly what attached, so nothing leaks.
      logger.warn(
        '[listenDesktopAuthSessionCommitted] Partially subscribed to auth session events',
        // `attached` plus WHY. This is the branch that matters most
        // diagnostically — one of the two commits is unobserved for the life of
        // the subscription, so a user can be re-armed on login but never on
        // refresh and the target silently goes stale for whichever lost.
        // `expected` was `results.length`, which is always 2: a constant
        // dressed as data, and the one field here that could never vary.
        { attached: unlisteners.length, errorName: rejectionName(results) }
      );
    }

    return () => {
      for (const unlisten of unlisteners) {
        unlisten();
      }
    };
  } catch (error) {
    // `error.name` only: per `.ai-context/telemetry-phi-pii-safety.md`, auth
    // and IdP failures routinely echo the account email or IdP subject in
    // `message`, and the Datadog transport applies no redaction to free text.
    logger.warn(
      '[listenDesktopAuthSessionCommitted] Failed to subscribe to auth session events',
      { errorName: error instanceof Error ? error.name : 'UnknownError' }
    );
    return () => {};
  }
}
